import React, { useState, useEffect, ChangeEvent } from "react";
import isNull from "lodash/isNull";
import map from "lodash/map";
import {
  Box,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
  Chip,
} from "@mui/material";

import GQLQueryStatusIndicator from "components/GQLQueryStatusIndicator";
import MoovsDialog from "components/MoovsDialog";
import { useScreenSize } from "globals/hooks";
import { errorRed, moovsBlue } from "design-system/colors";
import MoovsInfiniteScroll from "components/MoovsInfiniteScroll";
import useLoadFarmGnetAffiliatesQuery, { GnetAffiliate } from "globals/graphql/farming/useLoadFarmGnetAffiliatesQuery";
import { useDebounce } from "use-debounce";

type SharedGnetAffiliatesDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
};

type SelectAffiliateDialogProps = {
  isReadOnly?: false;
  onAcceptClick: (gnetAffiliate: GnetAffiliate) => void;
  acceptButtonText: string;
} & SharedGnetAffiliatesDialogProps;

type ReadOnlyDialogProps = {
  isReadOnly: true;
  onAcceptClick?: undefined;
  acceptButtonText?: string;
} & SharedGnetAffiliatesDialogProps;

type GnetAffiliatesDialogProps =
  | SelectAffiliateDialogProps
  | ReadOnlyDialogProps;

type GnetAffiliateCardProps = {
  affiliate: GnetAffiliate;
  isSelected: boolean;
  isReadOnly: boolean;
  handleAffiliateItemClick: (affiliate: GnetAffiliate) => void;
}

export function GnetAffiliateCard(props: GnetAffiliateCardProps) {
  const {
    affiliate,
    isSelected,
    isReadOnly,
    handleAffiliateItemClick,
  } = props;

  return (
    <Box
      my={1}
      key={affiliate.id}
      data-testid="gnet-affiliate-dialog-list-item"
>
  <Card 
    variant="outlined"
    sx={{
      border: isSelected
        ? `2px solid ${moovsBlue}`
        : undefined,
    }}
  >
    <CardActionArea
      onClick={() => !isReadOnly && handleAffiliateItemClick(affiliate)}
      disabled={isReadOnly}
    >
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" component="div">
                {affiliate.operatorName}
              </Typography>
              <Chip 
                label={`Gnet ID: ${affiliate.gnetId}`}
                size="small"
                color="primary"
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              {affiliate.serviceArea}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              {affiliate.operatorPhone}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              {affiliate.operatorEmail}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              Contact: {affiliate.contactName}
            </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </CardActionArea>
    </Card>
  </Box>
  );
}

function GnetAffiliatesDialog(props: GnetAffiliatesDialogProps) {
  const {
    open,
    title,
    acceptButtonText,
    onClose,
    onAcceptClick,
    isReadOnly,
  } = props;

  // hooks
  const { isMobileView } = useScreenSize();

  // state
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [hasInitialResults, setHasInitialResults] = useState<boolean | null>(null);
  const [debouncedInput] = useDebounce(globalSearch, 400);

  const {
    data: affiliatesData,
    error,
    refetch,
    loading,
    fetchMore,
  } = useLoadFarmGnetAffiliatesQuery({
    shouldSkip: !open,
  });

  // shaped data
  const affiliates: GnetAffiliate[] = map(
    affiliatesData?.loadFarmGnetAffiliates?.edges,
    "node"
  );

  // Filter affiliates based on search term
  const filteredAffiliates = debouncedInput
    ? affiliates.filter(
        (affiliate) =>
          affiliate.operatorName.toLowerCase().includes(debouncedInput.toLowerCase()) ||
          affiliate.serviceArea.toLowerCase().includes(debouncedInput.toLowerCase()) ||
          affiliate.gnetId.toLowerCase().includes(debouncedInput.toLowerCase())
      )
    : affiliates;

  // effects
  // whenever dialog reopens, reset back to initial state
  useEffect(() => {
    if (open) {
      refetch();
      setSelectedAffiliateId(null);
      setErrorMessage("");
      setHasInitialResults(null);
    }
  }, [open, refetch]);

  // sets initial value on if there are search results
  useEffect(() => {
    if (affiliatesData && isNull(hasInitialResults)) {
      setHasInitialResults(
        !!affiliatesData?.loadFarmGnetAffiliates?.edges.length
      );
    }
  }, [hasInitialResults, affiliatesData]);

  const handleAffiliateItemClick = (affiliate: GnetAffiliate) => {
    setSelectedAffiliateId(affiliate.id);
  };

  // event handlers
  const handleFetchMore = () => {
    fetchMore({
      variables: {
        cursor: affiliatesData?.loadFarmGnetAffiliates?.pageInfo.endCursor,
      },
    });
  };

  const handleSaveAddClick = () => {
    if (!selectedAffiliateId) {
      setErrorMessage("An affiliate must be selected.");
    } else {
      setErrorMessage("");
      if (onAcceptClick) {
        onAcceptClick(affiliates.find((affiliate: GnetAffiliate) => affiliate.id === selectedAffiliateId));
      }
    }
  };

  const hasAffiliates =
    !!affiliatesData?.loadFarmGnetAffiliates &&
    affiliatesData?.loadFarmGnetAffiliates?.edges?.length > 0;
  const noAffiliates =
    !!affiliatesData && hasInitialResults === false;
  const shouldRenderAffiliatesList = hasInitialResults && affiliatesData;

  return (
    <>
      <MoovsDialog
        scrollableId="scrollableGnetAffiliatesDialog"
        open={open}
        size="sm"
        onClose={onClose}
        dialogTitle={title}
        acceptButtonText={acceptButtonText}
        fixedFooter
        {...(hasAffiliates &&
          !!onAcceptClick && { onAccept: handleSaveAddClick })}
        {...(isReadOnly && {
          removeCloseButton: true,
          acceptButtonText: "Close",
          onAccept: onClose,
        })}
      >
        <Box minHeight="90vh">
          {error && (
            <Box height={160}>
              <GQLQueryStatusIndicator
                data={affiliatesData}
                loading={loading}
                error={error}
                name="Gnet Affiliates"
              />
            </Box>
          )}

          {errorMessage && (
            <Box p={2}>
              <Typography style={{ color: errorRed }}>
                {errorMessage}
              </Typography>
            </Box>
          )}

          {noAffiliates ? (
            <Box mt={7} p={3} textAlign="center">
              <Typography variant="h6">
                No Gnet affiliates found
              </Typography>
              <Typography variant="body1" color="textSecondary">
                There are no external operators with Griddnet IDs in your account.
              </Typography>
            </Box>
          ) : (
            <Box data-testid="gnet-affiliates-dialog" m={1}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    label={isMobileView ? "Search" : "Search affiliates"}
                    fullWidth
                    value={globalSearch || ""}
                    variant="outlined"
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setGlobalSearch(event.target.value);
                    }}
                  />
                </Grid>
              </Grid>
              {shouldRenderAffiliatesList && (
                <MoovsInfiniteScroll
                  loading={loading}
                  data={affiliatesData?.loadFarmGnetAffiliates}
                  next={handleFetchMore}
                  name="Gnet Affiliates"
                  scrollableId="scrollableGnetAffiliatesDialog"
                >
                  {filteredAffiliates.map((affiliate: GnetAffiliate) => (
                    <GnetAffiliateCard
                      key={affiliate.id}
                      affiliate={affiliate}
                      isSelected={selectedAffiliateId === affiliate.id}
                      isReadOnly={isReadOnly}
                      handleAffiliateItemClick={handleAffiliateItemClick}
                    />
                  ))}
                </MoovsInfiniteScroll>
              )}
            </Box>
          )}

          {loading && (
            <Box
              p={4}
              width="100%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <CircularProgress size={40} thickness={2} />
                <Box mt={0.5}>
                  <Typography>Loading Gnet affiliates...</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </MoovsDialog>
    </>
  );
}

export default GnetAffiliatesDialog;
